import { Box, Button, Grid, Link, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/styles';
import GraphQLErrors from '@/components/GraphQLErrors';
import { GraphQLErrors as GraphQLErrorsType } from '@apollo/client/errors';
import MultiStepForm, { FormStep } from '@/components/MultiStepForm';
import {
  formIntialState,
  lastStepValidationSchema,
  personalDetailsValidationSchema,
  siteDetailsValidationSchema,
  uiOnlyFields,
} from '@/constants/formInitConstants';
import { UIOnlyFields } from '@/types';
import { FormikProps, FormikValues } from 'formik';
import PersonalDetailsForm from '../SignupForms/PersonalDetailsForm';
import SiteDetailsForm from '../SignupForms/SiteDetailsForm';
import LastStepForm from '../SignupForms/LastStepForm';
import Image from 'next/image';
import playerImage from '../../../public/icons/videoPlayer.png';
import Confirmation from '@/components/NonInviteFlow/Confirmation';
import { useMultiStepFormContext } from '@/context/MultiStepFormContext';
import { SEARCH_SIMILAR_SITES } from '@/queries';
import { useLazyQuery, useMutation } from '@apollo/client';
import DuplicatesScreen from '@/components/NonInviteFlow/Duplicates';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { TRACK_USER_EVENT } from '@/mutations';
import {
  SearchSimilarSitesQuery,
  SearchSimilarSitesQueryVariables,
  Site,
  SiteApplicationInput,
  TrackUserEventMutation,
  TrackUserEventMutationVariables,
} from '@/gql/graphql';
import { TrackingEventsConstants } from '@/constants';
import FAQSection from '@/components/FAQSection';

export interface MainProps {
  graphQLErrors: GraphQLErrorsType | undefined;
  loading: boolean;
  onSubmit: (values: FormikValues, duplicateUsn?: string, callback?: VoidFunction) => void;
  isConfirmation: boolean;
  applicationId?: string;
  haveSiteVaultFlow?: boolean;
}

const Main = ({
  graphQLErrors,
  loading,
  onSubmit,
  isConfirmation,
  applicationId,
  haveSiteVaultFlow = false,
}: MainProps) => {
  const theme = useTheme();
  const router = useRouter();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [showDuplicatesScreen, setShowDuplicatesScreen] = useState(false);
  const [submittingApplication, setSubmittingApplication] = useState(false);
  const [selectedDuplicate, setSelectedDuplicate] = useState(false);

  const formContext = useMultiStepFormContext();
  const initialValues = {
    ...formIntialState,
    ...formContext?.formSnapshot,
  };
  const [searchDuplicateSites, { data, loading: duplicatesLoading }] = useLazyQuery<
    SearchSimilarSitesQuery,
    SearchSimilarSitesQueryVariables
  >(SEARCH_SIMILAR_SITES);

  const [fireTrackEvent, { loading: trackEventLoading }] = useMutation<
    TrackUserEventMutation,
    TrackUserEventMutationVariables
  >(TRACK_USER_EVENT);

  useEffect(() => {
    formContext?.resetForm();
  }, [haveSiteVaultFlow]);

  if (showDuplicatesScreen) {
    return (
      <DuplicatesScreen
        loading={submittingApplication}
        similarSites={data?.searchSimilarSites?.sites as Site[]}
        onDismiss={() => setShowDuplicatesScreen(false)}
        onSelected={(usn, notSvApp) => {
          if (notSvApp) {
            const vals = { ...formContext?.formSnapshot } as SiteApplicationInput;
            vals.usn = usn;
            formContext?.setFormSnapshot(vals);
            setShowDuplicatesScreen(false);
          } else {
            setSubmittingApplication(true);
            const vals = { ...formContext?.formSnapshot } as SiteApplicationInput;
            Object.keys(uiOnlyFields).forEach((field) => delete vals[field]);
            vals.duplicateUsn = usn;
            void (() => {
              onSubmit(vals, usn, () => {
                setSelectedDuplicate(true);
                setSubmittingApplication(false);
                setShowDuplicatesScreen(false);
              });
            })();
          }
        }}
        errors={graphQLErrors}
      />
    );
  }

  return (
    <Box>
      {isConfirmation || selectedDuplicate ? (
        <Confirmation fromDuplicateApp={selectedDuplicate} applicationId={applicationId} />
      ) : (
        <>
          <Box
            textAlign="center"
            mt={mdUp ? 9 : 4}
            mb={7}
            sx={{ color: (theme) => theme.palette.grey[800] }}>
            <Typography variant="h1" sx={{ fontWeight: 500 }} color="inherit">
              {haveSiteVaultFlow
                ? 'Get access to your SiteVault'
                : 'Register your site for Veeva SiteVault'}
            </Typography>
            <Typography
              variant="h4"
              sx={{ marginTop: (theme) => theme.spacing(1) }}
              color="inherit">
              {haveSiteVaultFlow
                ? 'Enter your information to help identify your site'
                : 'Join over 7,000 successful research sites across 85+ countries'}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item md={7} sm={12} xs={12} mb={4}>
              <GraphQLErrors graphQLErrors={graphQLErrors} />
              <Box mt={2}>
                <MultiStepForm
                  initialValues={initialValues}
                  isSubmitting={loading || duplicatesLoading}
                  onSubmit={(values) => {
                    onSubmit(values);
                  }}>
                  <FormStep
                    name="Personal Details"
                    validationSchema={personalDetailsValidationSchema}>
                    {(formik: FormikProps<SiteApplicationInput>) => {
                      return <PersonalDetailsForm formik={formik} />;
                    }}
                  </FormStep>

                  <FormStep
                    name="Site Details"
                    validationSchema={siteDetailsValidationSchema}
                    onSubmit={async (values: FormikValues) => {
                      const { data } = await searchDuplicateSites({
                        variables: {
                          query: {
                            company: values.organization as string,
                            email: values.email,
                            address: values.address1,
                            city: values.city,
                            country: values.country,
                            state: values.stateProvince,
                            postalCode: values.postalCode,
                            department: values.name,
                          },
                        },
                      });

                      if (data?.searchSimilarSites && data.searchSimilarSites.total > 0) {
                        setShowDuplicatesScreen(true);
                      }
                    }}>
                    {(formik: FormikProps<UIOnlyFields & SiteApplicationInput>) => {
                      return <SiteDetailsForm formik={formik} />;
                    }}
                  </FormStep>

                  <FormStep name="Submit" validationSchema={lastStepValidationSchema}>
                    {(formik: FormikProps<UIOnlyFields & SiteApplicationInput>) => {
                      return <LastStepForm formik={formik} />;
                    }}
                  </FormStep>
                </MultiStepForm>
              </Box>
            </Grid>

            <Grid item md={5} sm={12} xs={12}>
              <Box mt={mdUp ? 7 : 0} mb={6}>
                {!haveSiteVaultFlow && (
                  <>
                    <Box display="flex" alignItems="center" flexDirection="column" mb={2}>
                      <Box
                        sx={{
                          maxWidth: 354,
                          width: '100%',
                          borderRadius: 1,
                          padding: '16px 21px',
                          border: (theme) => `2px solid ${theme.palette.grey[200]}`,
                        }}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flexDirection="column">
                        <Typography sx={{ mb: 2 }} variant="h6">
                          Does your site already have SiteVault?
                        </Typography>

                        <Button
                          variant="text"
                          color="info"
                          disabled={trackEventLoading}
                          onClick={async () => {
                            await fireTrackEvent({
                              variables: {
                                eventType: TrackingEventsConstants.SV_CLICK_EVENT,
                                eventValue: 'Learn how to get access',
                                trackingId: null,
                                url: null,
                              },
                            });
                            void router.push('/?alreadyHaveSiteVault=true');
                          }}>
                          Learn how to get access
                        </Button>
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center" flexDirection="column">
                      <Typography sx={{ mb: 2 }} variant="h6">
                        See how SiteVault works
                      </Typography>
                      <Link
                        href={process.env.NEXT_PUBLIC_SITEVAULT_OVERVIEW_EXTERNAL_URL}
                        target="_blank">
                        <Image
                          alt="Player"
                          width={354}
                          src={playerImage}
                          placeholder="blur"
                          unoptimized
                          style={{
                            maxWidth: '100%',
                            height: 'auto',
                          }}
                        />
                      </Link>
                    </Box>
                  </>
                )}

                <FAQSection />
              </Box>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default Main;
